import { z } from 'zod';

export const salesGoalSchema = z.object({
  id: z.uuid(),
  year: z.number().int().min(2000).max(2100),
  month: z.number().int().min(1).max(12),
  accountId: z.uuid(),
  currency: z.string().length(3),
  amount: z.number().nonnegative(),
  organizationId: z.uuid(),
  createdOn: z.string().nullable(),
  modifiedOn: z.string().nullable(),
});

export type SalesGoal = z.infer<typeof salesGoalSchema>;

export const createSalesGoalInput = z.object({
  year: z.number().int().min(2000).max(2100),
  month: z.number().int().min(1).max(12),
  accountId: z.uuid(),
  currency: z.string().length(3),
  amount: z.number().nonnegative(),
});

export type CreateSalesGoalInput = z.infer<typeof createSalesGoalInput>;

export const updateSalesGoalInput = z.object({
  id: z.uuid(),
  year: z.number().int().min(2000).max(2100).optional(),
  month: z.number().int().min(1).max(12).optional(),
  accountId: z.uuid().optional(),
  currency: z.string().length(3).optional(),
  amount: z.number().nonnegative().optional(),
});

export type UpdateSalesGoalInput = z.infer<typeof updateSalesGoalInput>;

export const findSalesGoalsInput = z.object({
  offset: z.number().int().nonnegative().default(0),
  limit: z.number().int().positive().max(100).default(20),
  year: z.array(z.string()).optional(),
  accountId: z.array(z.uuid()).optional(),
  groupId: z.array(z.uuid()).optional(),
});

export type FindSalesGoalsInput = z.infer<typeof findSalesGoalsInput>;

export const salesGoalItemSchema = z.object({
  id: z.uuid(),
  year: z.number(),
  month: z.number(),
  currency: z.string(),
  amount: z.number(),
});

export type SalesGoalItem = z.infer<typeof salesGoalItemSchema>;

export const salesGoalRowSchema = z.object({
  accountId: z.uuid(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  teams: z.array(z.object({ id: z.uuid(), title: z.string() })).nullable(),
  goals: z.array(salesGoalItemSchema),
});

export type SalesGoalRow = z.infer<typeof salesGoalRowSchema>;

export const findSalesGoalsResultSchema = z.object({
  data: z.array(salesGoalRowSchema),
  count: z.number().int().nonnegative(),
  countAll: z.number().int().nonnegative(),
});

export type FindSalesGoalsResult = z.infer<typeof findSalesGoalsResultSchema>;

export const salesGoalFilterCountsSchema = z.object({
  accounts: z.array(
    z.object({
      id: z.uuid(),
      title: z.string(),
      count: z.number().int().nonnegative(),
    })
  ),
  teams: z.array(
    z.object({
      id: z.uuid(),
      title: z.string(),
      count: z.number().int().nonnegative(),
    })
  ),
});

export type SalesGoalFilterCounts = z.infer<typeof salesGoalFilterCountsSchema>;

export const refreshSalesGoalsResultSchema = z.object({
  success: z.boolean(),
  elapsedMs: z.number().nonnegative(),
});

export type RefreshSalesGoalsResult = z.infer<
  typeof refreshSalesGoalsResultSchema
>;
